import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { UI_LANGUAGE_STORAGE_KEY } from './constants/uiLanguages';
import { getPathBoundUiLanguage, getUrlDefaultUiLanguage } from './lib/uiRouteLanguage';

import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enLearning from './locales/en/learning.json';
import enPages from './locales/en/pages.json';
import enDashboard from './locales/en/dashboard.json';
import enOnboarding from './locales/en/onboarding.json';

import esCommon from './locales/es/common.json';
import esAuth from './locales/es/auth.json';
import esLearning from './locales/es/learning.json';
import esPages from './locales/es/pages.json';
import esDashboard from './locales/es/dashboard.json';
import esOnboarding from './locales/es/onboarding.json';

import noCommon from './locales/no/common.json';
import noAuth from './locales/no/auth.json';
import noLearning from './locales/no/learning.json';
import noPages from './locales/no/pages.json';
import noDashboard from './locales/no/dashboard.json';
import noOnboarding from './locales/no/onboarding.json';

import deCommon from './locales/de/common.json';
import deAuth from './locales/de/auth.json';
import deLearning from './locales/de/learning.json';
import dePages from './locales/de/pages.json';
import deDashboard from './locales/de/dashboard.json';

import frCommon from './locales/fr/common.json';
import frAuth from './locales/fr/auth.json';
import frLearning from './locales/fr/learning.json';
import frPages from './locales/fr/pages.json';
import frDashboard from './locales/fr/dashboard.json';

import ptCommon from './locales/pt/common.json';
import ptAuth from './locales/pt/auth.json';
import ptLearning from './locales/pt/learning.json';
import ptPages from './locales/pt/pages.json';
import ptDashboard from './locales/pt/dashboard.json';

import itCommon from './locales/it/common.json';
import itAuth from './locales/it/auth.json';
import itLearning from './locales/it/learning.json';
import itPages from './locales/it/pages.json';
import itDashboard from './locales/it/dashboard.json';

import ruCommon from './locales/ru/common.json';
import ruAuth from './locales/ru/auth.json';
import ruLearning from './locales/ru/learning.json';
import ruPages from './locales/ru/pages.json';
import ruDashboard from './locales/ru/dashboard.json';

import nlCommon from './locales/nl/common.json';
import nlAuth from './locales/nl/auth.json';
import nlLearning from './locales/nl/learning.json';
import nlPages from './locales/nl/pages.json';
import nlDashboard from './locales/nl/dashboard.json';

import plCommon from './locales/pl/common.json';
import plAuth from './locales/pl/auth.json';
import plLearning from './locales/pl/learning.json';
import plPages from './locales/pl/pages.json';
import plDashboard from './locales/pl/dashboard.json';

import zhCommon from './locales/zh-CN/common.json';
import zhAuth from './locales/zh-CN/auth.json';
import zhLearning from './locales/zh-CN/learning.json';
import zhPages from './locales/zh-CN/pages.json';
import zhDashboard from './locales/zh-CN/dashboard.json';

import jaCommon from './locales/ja/common.json';
import jaAuth from './locales/ja/auth.json';
import jaLearning from './locales/ja/learning.json';
import jaPages from './locales/ja/pages.json';
import jaDashboard from './locales/ja/dashboard.json';

import arCommon from './locales/ar/common.json';
import arAuth from './locales/ar/auth.json';
import arLearning from './locales/ar/learning.json';
import arPages from './locales/ar/pages.json';
import arDashboard from './locales/ar/dashboard.json';

import trCommon from './locales/tr/common.json';
import trAuth from './locales/tr/auth.json';
import trLearning from './locales/tr/learning.json';
import trPages from './locales/tr/pages.json';
import trDashboard from './locales/tr/dashboard.json';

const supportedLngs = ['en', 'es', 'no', 'de', 'fr', 'pt', 'it', 'ru', 'nl', 'pl', 'tr', 'zh-CN', 'ja', 'ar'];

const resources = {
  en: { common: enCommon, auth: enAuth, learning: enLearning, pages: enPages, dashboard: enDashboard, onboarding: enOnboarding },
  es: { common: esCommon, auth: esAuth, learning: esLearning, pages: esPages, dashboard: esDashboard, onboarding: esOnboarding },
  no: { common: noCommon, auth: noAuth, learning: noLearning, pages: noPages, dashboard: noDashboard, onboarding: noOnboarding },
  de: { common: deCommon, auth: deAuth, learning: deLearning, pages: dePages, dashboard: deDashboard },
  fr: { common: frCommon, auth: frAuth, learning: frLearning, pages: frPages, dashboard: frDashboard },
  pt: { common: ptCommon, auth: ptAuth, learning: ptLearning, pages: ptPages, dashboard: ptDashboard },
  it: { common: itCommon, auth: itAuth, learning: itLearning, pages: itPages, dashboard: itDashboard },
  ru: { common: ruCommon, auth: ruAuth, learning: ruLearning, pages: ruPages, dashboard: ruDashboard },
  nl: { common: nlCommon, auth: nlAuth, learning: nlLearning, pages: nlPages, dashboard: nlDashboard },
  pl: { common: plCommon, auth: plAuth, learning: plLearning, pages: plPages, dashboard: plDashboard },
  'zh-CN': { common: zhCommon, auth: zhAuth, learning: zhLearning, pages: zhPages, dashboard: zhDashboard },
  ja: { common: jaCommon, auth: jaAuth, learning: jaLearning, pages: jaPages, dashboard: jaDashboard },
  ar: { common: arCommon, auth: arAuth, learning: arLearning, pages: arPages, dashboard: arDashboard },
  tr: { common: trCommon, auth: trAuth, learning: trLearning, pages: trPages, dashboard: trDashboard }
};

function normalizeDetectedLanguage(language) {
  const normalized = String(language || '').trim().toLowerCase();
  if (!normalized) return 'en';
  if (normalized.startsWith('nb') || normalized.startsWith('nn') || normalized.startsWith('no')) return 'no';
  if (normalized.startsWith('zh')) return 'zh-CN';
  return language;
}

function detectInitialUiLanguage() {
  if (typeof window === 'undefined') {
    return 'en';
  }

  const pathname = window.location?.pathname || '/';
  const pathLanguage = getPathBoundUiLanguage(pathname);
  if (pathLanguage && supportedLngs.includes(pathLanguage)) {
    return pathLanguage;
  }

  try {
    const storedLanguage = localStorage.getItem(UI_LANGUAGE_STORAGE_KEY);
    if (storedLanguage) {
      return normalizeDetectedLanguage(storedLanguage);
    }
  } catch {
    // Ignore storage failures and continue with navigator/html hints.
  }

  // No saved preference: the URL decides (e.g. /methodology is English), so
  // first-time visitors and search engines see the language the URL declares.
  const urlDefaultLanguage = getUrlDefaultUiLanguage(pathname);
  if (urlDefaultLanguage && supportedLngs.includes(urlDefaultLanguage)) {
    return urlDefaultLanguage;
  }

  const navigatorLanguage =
    (Array.isArray(window.navigator?.languages) && window.navigator.languages[0])
    || window.navigator?.language;
  if (navigatorLanguage) {
    return normalizeDetectedLanguage(navigatorLanguage);
  }

  const htmlLanguage = window.document?.documentElement?.lang;
  if (htmlLanguage) {
    return normalizeDetectedLanguage(htmlLanguage);
  }

  return 'en';
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    lng: detectInitialUiLanguage(),
    fallbackLng: 'en',
    supportedLngs,
    nonExplicitSupportedLngs: true,
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'aprendos_language',
      checkWhitelist: true,
      convertDetectedLanguage: normalizeDetectedLanguage,
    },
    defaultNS: 'common',
    ns: ['common', 'auth', 'learning', 'pages', 'dashboard', 'onboarding'],
    debug: false,
    keySeparator: '.',
    nsSeparator: ':',
    interpolation: {
      escapeValue: false,
    },
    returnKeyIfEmpty: true,
    returnEmptyString: false,
    react: {
      useSuspense: false,
    },
  });

export default i18n;
